/**
 * The paid service around a studio confirmation request — OND-60.
 *
 * NINE ROUTES ON ONE RESOURCE AND NOT ON `verification-requests`, which is the
 * one thing worth knowing from the client's side: asking a studio and answering
 * a studio are `provenance.ts` and `verifications.ts`, and this is the service
 * wrapped round them. A reader on either side of a case uses the same read.
 */
import type {
  AssignAttributionReviewerRequest,
  AttributionFeeLogResponse,
  AttributionReviewersResponse,
  AttributionServiceCasesResponse,
  AttributionServiceLogResponse,
  CorrectAttributionFeeRequest,
  ExtendAttributionTargetRequest,
  RecordAttributionFeePaidRequest,
  RequestAttributionEvidenceRequest,
} from "@ondo/shared";

import { request } from "./request.ts";

/**
 * Every case the caller is a party to, from either side.
 *
 * NO `principal` PARAMETER, unlike the verifier's inbox, and the difference is
 * real rather than an omission. That read answers "what has been asked OF this
 * workspace" and has to be scoped, because a request addressed to a gallery has
 * no business in a collector's personal inbox. This one answers "which cases am
 * I a party to", and the asker's own commissions and the studio's own queue are
 * both the caller's — `ondo.attribution_service_cases()` admits the entry's
 * curator and anybody at viewer on the studio, and `iAmTheStudio` on each row
 * says which.
 */
export const fetchAttributionServiceCases = () =>
  request<AttributionServiceCasesResponse>("/attribution-service/cases").then(
    (body) => body.cases,
  );

/** One case's service log, oldest first: promised, asked for more, revised. */
export const fetchAttributionServiceLog = (requestId: string) =>
  request<AttributionServiceLogResponse>(
    `/attribution-service/cases/${requestId}/log`,
  ).then((body) => body.steps);

/** Who has held the case, newest first, with the ended rows kept. */
export const fetchAttributionReviewers = (requestId: string) =>
  request<AttributionReviewersResponse>(
    `/attribution-service/cases/${requestId}/reviewers`,
  ).then((body) => body.reviewers);

/** Every correction appended to one fee, oldest first. */
export const fetchAttributionFeeLog = (feeId: string) =>
  request<AttributionFeeLogResponse>(`/attribution-service/fees/${feeId}/log`).then(
    (body) => body.corrections,
  );

/**
 * The studio asks for more. It does not answer and it raises no fee.
 */
export const requestAttributionEvidence = (
  requestId: string,
  body: RequestAttributionEvidenceRequest,
) => request<void>(`/attribution-service/cases/${requestId}/evidence`, body);

/** A revised target with its reason. It must be later than the standing one. */
export const extendAttributionTarget = (
  requestId: string,
  body: ExtendAttributionTargetRequest,
) => request<void>(`/attribution-service/cases/${requestId}/extension`, body);

/**
 * Assign, reassign or escalate.
 *
 * One call for all three, matching the one door. It gives the reviewer a
 * question to look at and no power to answer it.
 */
export const assignAttributionReviewer = (
  requestId: string,
  body: AssignAttributionReviewerRequest,
) => request<void>(`/attribution-service/cases/${requestId}/reviewer`, body);

/** The studio records that the money arrived. An assertion, never an observation. */
export const recordAttributionFeePaid = (
  feeId: string,
  body: RecordAttributionFeePaidRequest,
) => request<void>(`/attribution-service/fees/${feeId}/paid`, body);

/** Appends a void, a refund-due or a recorded-refund line. The fee survives whole. */
export const correctAttributionFee = (feeId: string, body: CorrectAttributionFeeRequest) =>
  request<void>(`/attribution-service/fees/${feeId}/corrections`, body);
